import { api } from "assets/scripts/utilities/api-clients";
import { useChangeCase } from "@vueuse/integrations/useChangeCase";

const mapIds = (items) => items?.map((item) => item?._id || item);

const prepareBusinessType = (businessType) =>
  businessType
    ? { name: businessType?.name, code: businessType?.code }
    : undefined;

const prepareCrisil = (crisil) =>
  crisil
    ? [
        {
          crisil: `${crisil?.name} (${crisil?.indicator})`,
          ranking: crisil?.ranking,
        },
      ]
    : undefined;

const formatDirectors = (directorsList, businessCode) => {
  if (businessCode === "PLC") return [];

  const formatted = directorsList.map((director, index) => {
    const phoneNumber = Number(director.phone);

    return {
      ...director,
      phone: phoneNumber || "",
      name: useChangeCase(director.name, "sentenceCase")?.value,
      is_primary: index === 0,
    };
  });

  return formatted;
};

const prepareUser = (user) => {
  let clonedUser = { ...user };

  const {
    account_owner,
    associated_agent,
    business_type,
    crisil,
    categories,
    products,
    directors,
    is_msme_registered,
    fssai,
  } = clonedUser;

  if (directors) {
    clonedUser.directors = formatDirectors(directors, business_type?.code);
  }

  if (is_msme_registered === false) {
    clonedUser.is_trade_msme = false;

    clonedUser.udyam_aadhar = "";
  }

  if ("fssai" in user && !fssai?.trim()) {
    clonedUser.fssai_expiration_date = null;
  }

  return {
    ...clonedUser,
    account_owner: account_owner?._id || account_owner,
    associated_agent: associated_agent?._id || associated_agent,
    categories: mapIds(categories),
    products: mapIds(products),
    business_type: prepareBusinessType(business_type),
    credit_ratings: prepareCrisil(crisil),
  };
};

const prepareCreditRatingsPayload = (ratings) => {
  return ratings.reduce((acc, item) => {
    const { agency, ...rest } = item;

    const allFieldsNull = Object.values(rest).every((value) => value == null);

    if (!agency || allFieldsNull) {
      return acc;
    }

    const existingIndex = acc.findIndex((entry) => entry[agency]);

    if (existingIndex === -1) {
      acc.push({ [agency]: { ...rest } });
    } else {
      acc[existingIndex][agency] = { ...rest };
    }

    return acc;
  }, []);
};

export const get = async (id) => {
  const response = await api.get(`/kam/users/${id}`);

  return response;
};

export const getPortfolio = async (id) => {
  const response = await api.get(`/user/portfolio/${id}`);

  return response;
};

export const index = async (options = {}) => {
  const response = await api.get("/kam/users", options);

  return response;
};

export const post = async (user) => {
  const response = await api.post("/kam/users", user);

  return response;
};

export const put = async (user) => {
  const response = await api.put(`/kam/users/${user._id}`, prepareUser(user));

  return response;
};

export const putStatus = async ({
  _id,
  is_active,
  organisations,
  role,
  reason,
} = {}) => {
  const payload = {
    is_active,
    organisationId: organisations?._id || null,
    role,
    reason,
  };
  const response = await api.put(`/kam/user-status/${_id}`, payload);

  return response;
};

export const putBusinessDetails = async (user) => {
  const response = await api.put(
    `/kam/business-details/${user._id}`,
    prepareUser(user),
  );

  return response;
};

export const getDocumentsByUserId = async (document = {}) => {
  const response = await api.get(
    `/kam/documents/${document.user_id}`,
    document,
  );

  return response;
};

export const putDocumentRequirement = async (document = {}) => {
  const response = await api.put(
    `/kam/documents/requirement/${document.user_id}`,
    document,
  );

  return response;
};

export const putDocumentStatus = async (document = {}) => {
  const response = await api.put(`/kam/documents/status`, document);

  return response;
};

export const getDocumentZip = async (options = {}) => {
  const response = await api.get(`/kam/download/multiple`, options);

  return response;
};

export const getClonedDocument = async (
  file,
  {
    headers: {
      "Content-Type": contentType = "multipart/form-data",
      ...restHeaders
    } = {},
    ...restOptions
  } = {},
) => {
  const response = await api.post(`/kam/documents`, file, {
    headers: { "Content-Type": contentType, ...restHeaders },
    ...restOptions,
  });

  return response;
};

export const getCreditVerification = async (options) => {
  const response = await api.get(
    `/kam/credit-evaluation/${options.user_id}`,
    options,
  );

  return response;
};

export const putVerificationData = async (verificationData) => {
  const response = await api.put(
    `/kam/user-verification/${verificationData.user_id}`,
    verificationData,
  );

  return response;
};

export const putCreditReportForApproval = async (report) => {
  const response = await api.put(
    `/kam/sent/approval/${report.user_id}`,
    report,
  );

  return response;
};

export const putCreditReportStatusApproved = async (report) => {
  const response = await api.put(`/kam/approved/${report.user_id}`, report);

  return response;
};

export const putCreditReportStatusRejected = async (report) => {
  const response = await api.put(`/kam/reject/${report.user_id}`, report);

  return response;
};

export const count = async (options) => {
  const response = await api.get("/kam/users/count", options);

  return response;
};

export const getDocumentMaster = async (code) => {
  const response = await api.get(`/kam/document/master?code=${code}`);

  return response;
};

export const getAssociatedUsers = async (options) => {
  const { params } = options;
  const { isAgent, user_id, ...restParams } = params;
  const url = isAgent
    ? `/kam/agent-users/${user_id}`
    : `/kam/customer/${user_id}`;
  const response = await api.get(url, { ...options, params: restParams });

  return response;
};

export const getReport = async ({ userId, reportType }) => {
  try {
    const response = await api.get(`/kam/${reportType}/${userId}`);

    return response;
  } catch (error) {
    console.error(error);
  }
};

export const putApplicationStatus = async (userId, payload) => {
  const response = await api.put(
    `/kam/user/${userId}/application-status`,
    payload,
  );

  return response;
};

export const putApplicationStage = async (userId, payload) => {
  const response = await api.put(
    `/kam/user/${userId}/application-stage`,
    payload,
  );

  return response;
};

export const putConsentInfo = async (userId, consentInfo) => {
  const response = await api.put(`/kam/user/${userId}/consent`, consentInfo);

  return response;
};

export const putDocumentSignatureStatus = async (userId, document) => {
  const response = await api.put(
    `/kam/sign-documents/status/${userId}`,
    document,
  );

  return response;
};

export const getDocumentSignatureStatus = async (userId) => {
  const response = await api.get(`/kam/sign-document/${userId}`);

  return response;
};

export const getCourtCasesReport = async (userId) => {
  const response = await api.get(`/kam/court-cases-report/${userId}`);

  return response;
};

export const getGstFilingReport = async (userId, gstin) => {
  const response = await api.get(`/kam/gst-report/${userId}/${gstin}`);

  return response;
};

export const getCreditReportByDirectorId = async (directorId) => {
  const response = await api.get(`/kam/credit-report/${directorId}`);

  return response;
};

export const putCreditRating = async (userId, payload) => {
  const formattedPayload = {
    credit_ratings: prepareCreditRatingsPayload(payload),
  };
  const response = await api.put(
    `/kam/credit-rating/${userId}`,
    formattedPayload,
  );

  return response;
};

export const putDirectorDetail = async (userId, director) => {
  const response = await api.put(`/kam/credit-score/${userId}`, { director });

  return response;
};

export const getLinkedUsers = async (options = {}) => {
  const response = await api.get("/kam/link-user", options);

  return response;
};

export const putLinkAssociatedUser = async (userId, payload) => {
  const { isAgent, ...rest } = payload;
  const url = `/kam/${isAgent ? "link-agent" : "link-user"}/${userId}`;

  const response = await api.put(url, rest);

  return response;
};

export const putUnLinkAssociatedUser = async (userId, payload) => {
  const { isAgent, ...rest } = payload;
  const url = `/kam/${isAgent ? "unlink-agent" : "unlink-user"}/${userId}`;

  const response = await api.put(url, rest);

  return response;
};

export const validateUser = async (payload) => {
  const response = await api.post(`/kam/users/checkUserValidation`, payload);

  return response;
};

export const putOperationalStatus = async (userId, payload) => {
  const response = await api.put(
    `/kam/user-operational-status/${userId}`,
    payload,
  );

  return response;
};

export const getApproversList = async () => {
  const response = await api.get(`/kam/approvers-user-list`);

  return response;
};

export const postValidateUserWithStatus = async (payload) => {
  const response = await api.post(
    `/kam/users/UserCombinationValidation`,
    payload,
  );

  return response;
};

export const getDummyBuyer = async () => {
  const response = await api.get("/kam/dummy-buyer");

  return response;
};

export const putValidateDirectorName = async (director) => {
  const response = await api.put("/kam/users/validate/director-name", director);

  return response;
};

export const putVerifyUserVerificationCode = async (payload) => {
  const response = await api.put("/kam/user/verify/otp", payload);

  return response;
};

export const putResendVerificationCode = async (payload) => {
  const response = await api.put("/kam/user/resend-verification-code", payload);

  return response;
};

export const postValidateBusinessName = async (payload) => {
  const response = await api.post(
    "/kam/user/check-duplicate-business-name",
    payload,
  );

  return response;
};

export const postUdyamAadharDetails = async (payload) => {
  const response = await api.post("/kam/user/udyam-aadhar-details", payload);

  return response;
};

export const postuploadUdyamDocument = async (payload) => {
  const response = await api.post("/kam/user/udyam-aadhar-upload", payload);

  return response;
};

export const putLegalCaseStatus = async (payload) => {
  const response = await api.put("/kam/user/update-legal-case", payload);

  return response;
};
